import { api } from '../config/api';
import { Colis, StatutColis } from '../types';

export const colisService = {
  // Créer un colis
  create: async (data: Partial<Colis>) => {
    const response = await api.post('/colis', data);
    return response.data;
  },

  // Liste des colis
  getAll: async (filters?: { statut?: string; livreur_id?: string }) => {
    const response = await api.get('/colis', { params: filters });
    return response.data;
  },

  // Détails d'un colis
  getOne: async (id: string) => {
    const response = await api.get(`/colis/${id}`);
    return response.data;
  },

  // Mes envois (module Particulier/livreur dédié) — scopé au particulier connecté
  getMesEnvois: async (): Promise<Colis[]> => {
    const response = await api.get('/colis/mes-envois');
    return response.data;
  },

  // Suivi public par tracking
  trackByNumber: async (tracking_number: string) => {
    const response = await api.get(`/colis/tracking/${tracking_number}`);
    return response.data;
  },

  // Créneau de livraison souhaité — self-service destinataire, page de suivi
  // publique (module E-Commerce, spéc. 25/09/26). Sans JWT : le tracking
  // number fait office de clé d'accès (même pattern que validateOtpPublic).
  setDeliveryPreferencePublic: async (
    tracking_number: string,
    data: { date_souhaitee?: string; creneau?: string; note?: string },
  ) => {
    const response = await api.patch(`/colis/public/track/${tracking_number}/preference-livraison`, data);
    return response.data as {
      success: boolean;
      livraison_date_souhaitee: string | null;
      livraison_creneau_souhaite: string | null;
      livraison_note_destinataire: string | null;
    };
  },

  // Mise à jour partielle (montants article/livraison, coordonnées, etc.)
  update: async (id: string, data: Partial<Colis>) => {
    const response = await api.patch(`/colis/${id}`, data);
    return response.data;
  },

  // Mettre à jour le statut
  updateStatut: async (
    id: string,
    data: {
      statut: StatutColis;
      localisation?: string;
      commentaire?: string;
      photo_url?: string;
      signature_url?: string;
      etape_douane?: string;
      otp_code?: string;
      receptionnaire_nom?: string;
      date_reprogrammation?: string;
      // Dernier recours si l'OTP n'a pas pu être délivré (spéc. 28/09/26) —
      // n'est accepté par le serveur que si colis.otp_delivery_failed est vrai,
      // et exige aussi photo_url (preuve de remise).
      otp_bypass_reason?: string;
    }
  ) => {
    const response = await api.patch(`/colis/${id}/statut`, data);
    return response.data;
  },

  // Régénérer le code OTP de livraison (bloqué ou non reçu)
  regenerateOtp: async (id: string) => {
    const response = await api.patch(`/colis/${id}/otp/regenerate`);
    // `delivered` distingue "la requête a marché" de "le code a réellement
    // été envoyé" — avant, l'UI affichait "code envoyé" même en cas d'échec
    // total de délivrance (spéc. 28/09/26).
    return response.data as { success: boolean; delivered: boolean };
  },

  // Frais de garde/magasinage (Module Transport Interurbain — spéc. MAJ 13/09)
  getFraisGarde: async (id: string) => {
    const response = await api.get(`/colis/${id}/frais-garde`);
    return response.data as {
      disponible_depuis: string;
      jours_ecoules: number;
      franchise_jours: number;
      jours_facturables: number;
      tarif_par_jour: number;
      montant_suggere: number;
      deja_applique: boolean;
      montant_applique: number | null;
    };
  },

  appliquerFraisGarde: async (id: string, montant?: number) => {
    const response = await api.patch(`/colis/${id}/frais-garde/appliquer`, { montant });
    return response.data;
  },

  // Recours destinataire — saisie du code OTP sur la page de suivi publique
  validateOtpPublic: async (tracking_number: string, code: string) => {
    const response = await api.post(`/colis/public/track/${tracking_number}/otp/validate`, { code });
    return response.data as { success: boolean };
  },

  // Historique d'un colis
  getHistorique: async (id: string) => {
    const response = await api.get(`/colis/${id}/historique`);
    return response.data;
  },
  
  // Assigner un livreur
  assignLivreur: async (colisId: string, livreurId: string) => {
    const response = await api.patch(`/colis/${colisId}/assign-livreur`, {
      livreur_id: livreurId,
    });
    return response.data;
  },

  /** Courses non affectées visibles par le livreur connecté (modèle pull) */
  getDisponibles: async () => {
    const response = await api.get('/colis/disponibles');
    return response.data;
  },

  /** Le livreur connecté prend lui-même une course non affectée */
  claim: async (colisId: string) => {
    const response = await api.patch(`/colis/${colisId}/claim`);
    return response.data;
  },

  // Désassigner un livreur
  unassignLivreur: async (colisId: string) => {
    const response = await api.patch(`/colis/${colisId}/unassign-livreur`);
    return response.data;
  },

  // Le livreur décline la livraison qui lui a été affectée
  declineLivreur: async (colisId: string, reason: string) => {
    const response = await api.patch(`/colis/${colisId}/decline-livreur`, { reason });
    return response.data;
  },

  // Upload photo principale du colis (photo_colis_url) — renvoie l'URL et met à jour le colis
  uploadPhoto: async (colisId: string, file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/colis/${colisId}/photo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.url as string;
  },

  // Upload photo de livraison ou signature (renvoie l'URL)
  uploadDeliveryFile: async (colisId: string, file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/colis/${colisId}/upload-file`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.url as string;
  },

  // Télécharger le template Excel pour l'import en masse
  downloadTemplate: async (): Promise<void> => {
    const response = await api.get('/colis/import/template', {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'template-import-colis.xlsx');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  // Import en masse depuis un fichier Excel
  importFile: async (file: File): Promise<{ created: number; skipped: number; errors: { row: number; message: string }[] }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/colis/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // Mes livraisons (pour les livreurs)
  getMesLivraisons: async () => {
    const response = await api.get('/colis/mes-livraisons');
    return response.data;
  },

  // Export CSV (avec filtres optionnels)
  exportCSV: async (filters?: Record<string, string>): Promise<void> => {
    const response = await api.get('/colis/export/csv', {
      params: filters,
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
    const link = document.createElement('a');
    link.href = url;
    const date = new Date().toISOString().split('T')[0];
    link.setAttribute('download', `export-colis-${date}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  // Export Excel (avec filtres optionnels)
  exportExcel: async (filters?: Record<string, string>): Promise<void> => {
    const response = await api.get('/colis/export/excel', {
      params: filters,
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(
      new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }),
    );
    const link = document.createElement('a');
    link.href = url;
    const date = new Date().toISOString().split('T')[0];
    link.setAttribute('download', `export-colis-${date}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  // Marquer COD comme collecté
  markCodCollecte: async (id: string, payment_method?: string) => {
    const response = await api.patch(`/colis/${id}/cod-collecte`, { payment_method });
    return response.data;
  },

  // Feedback public (sans auth)
  getFeedback: async (token: string) => {
    const response = await api.get(`/colis/feedback/${token}`);
    return response.data as { tracking_number: string; destinataire_nom: string; livreur_nom: string | null; already_rated: boolean };
  },

  submitFeedback: async (token: string, data: { note: number; commentaire?: string }) => {
    const response = await api.post(`/colis/feedback/${token}`, data);
    return response.data;
  },

  // QR Label — retourne un blob URL
  getQrLabelUrl: (id: string): string => {
    const token = localStorage.getItem('access_token');
    return `${api.defaults.baseURL}/colis/${id}/qr-label?token=${token}`;
  },

  getQrLabelBlob: async (id: string): Promise<string> => {
    const response = await api.get(`/colis/${id}/qr-label`, { responseType: 'blob' });
    return window.URL.createObjectURL(new Blob([response.data], { type: 'image/png' }));
  },

  // Étiquette PDF complète (logo, destinataire, montant, QR + code-barres)
  getEtiquetteBlob: async (id: string): Promise<string> => {
    const response = await api.get(`/colis/${id}/etiquette`, { responseType: 'blob' });
    return window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
  },

  bulkAssign: async (colis_ids: string[], livreur_id: string | null) => {
    const response = await api.patch('/colis/bulk-assign', { colis_ids, livreur_id });
    return response.data as { updated: number };
  },
};
